import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { track } from "./lib/api";
import { captureAttribution } from "./lib/catalog";

import Layout from "./components/Layout";
import Home from "./pages/Home";
import FreeScan from "./pages/FreeScan";
import Results from "./pages/Results";
import Pricing from "./pages/Pricing";
import Product from "./pages/Product";
import HowItWorks from "./pages/HowItWorks";
import ContentPage from "./pages/ContentPage";
import Checkout from "./pages/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import Onboarding from "./pages/Onboarding";
import ThankYou from "./pages/ThankYou";
import SampleReport from "./pages/SampleReport";
import Demos from "./pages/Demos";

const withLayout = (el) => <Layout>{el}</Layout>;

function AppRouter() {
  const location = useLocation();
  useEffect(() => { captureAttribution(); }, []);
  useEffect(() => {
    const utm = Object.fromEntries([...new URLSearchParams(location.search)].filter(([k]) => k.startsWith("utm_")));
    track("landing.viewed", { path: location.pathname, ...utm });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={withLayout(<Home />)} />
      <Route path="/free-scan" element={withLayout(<FreeScan />)} />
      <Route path="/results/:scanId" element={<Results />} />
      <Route path="/score/:scanId" element={<Results />} />
      <Route path="/blueprint" element={withLayout(<Product code="blueprint" />)} />
      <Route path="/sample-report" element={withLayout(<SampleReport />)} />
      <Route path="/demos" element={withLayout(<Demos />)} />
      <Route path="/ai-visibility-vs-ai-transactions" element={withLayout(<ContentPage slug="ai-visibility-vs-ai-transactions" />)} />
      <Route path="/thank-you/:product" element={withLayout(<ThankYou />)} />
      <Route path="/blog/:slug" element={withLayout(<ContentPage slug="blog" />)} />
      <Route path="/pricing" element={withLayout(<Pricing />)} />
      <Route path="/how-it-works" element={withLayout(<HowItWorks />)} />
      <Route path="/get-found" element={withLayout(<Product code="get_found" />)} />
      <Route path="/agentic-ready" element={withLayout(<Product code="agentic_ready" />)} />
      <Route path="/autonomous-commerce" element={withLayout(<Product code="autonomous_commerce" />)} />
      <Route path="/ai-commerce" element={withLayout(<ContentPage slug="ai-commerce" />)} />
      <Route path="/aeo-geo" element={withLayout(<ContentPage slug="aeo-geo" />)} />
      <Route path="/machine-payments" element={withLayout(<ContentPage slug="machine-payments" />)} />
      <Route path="/x402" element={withLayout(<ContentPage slug="x402" />)} />
      <Route path="/security" element={withLayout(<ContentPage slug="security" />)} />
      <Route path="/resources" element={withLayout(<ContentPage slug="resources" />)} />
      <Route path="/blog" element={withLayout(<ContentPage slug="blog" />)} />
      <Route path="/contact" element={withLayout(<ContentPage slug="contact" />)} />
      <Route path="/privacy" element={withLayout(<ContentPage slug="privacy" />)} />
      <Route path="/terms" element={withLayout(<ContentPage slug="terms" />)} />
      <Route path="/refund-policy" element={withLayout(<ContentPage slug="refund-policy" />)} />
      <Route path="/sources" element={withLayout(<ContentPage slug="sources" />)} />
      <Route path="/checkout/:planId" element={withLayout(<Checkout />)} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
